import moment from 'moment';
import React, { useContext, useEffect, useState } from 'react';
import { Button, Form, Modal } from 'react-bootstrap';
import { withTranslation } from "react-i18next";
import { toast } from 'react-toastify';
import { LoaderContext } from '../../../App';
import MonthlyPeriodSelector from '../../../components/ReportingPeriod/MonthlyPeriodSelector';
import { FormFrequency } from '../../../core/constants/common';
import CalendarEventService from '../../../core/service/CalendarEventService';
import Utils from '../../../core/utilities/Utils';

const OrganizationCalendarModal = ({ t, eventId, onCloseCallback, onUpdate }) => {

	const { setLoaderDisplay } = useContext(LoaderContext);
	const [show, setShow] = useState(!!eventId);
	const [config, setConfig] = useState({
		recurringDay: null,
		expiringDay: null,
		frequency: '',
		enabled: false,
		type: '',
		startDate: '',
		endDate: ''
	});

	useEffect(() => {
		if (!eventId) return;

		getEvent();
	}, [eventId]);

	const getEvent = () => {
		setLoaderDisplay(true);
		CalendarEventService.getEvent(eventId).then(res => {
			if (res.status !== 200) return;

			setLoaderDisplay(false);
			setConfig({
				...config,
				recurringDay: res.data?.recurringDay,
				expiringDay: res.data?.expiringDay,
				frequency: res.data?.frequency,
				enabled: res.data?.active,
				type: res.data?.type,
				startDate: res.data?.startDate,
				endDate: res.data?.endDate
			});
		}).catch(err => {
			setLoaderDisplay(false);
		});
	};

	const updateEvent = () => {
		CalendarEventService.updateEvent(eventId, {
			recurringDay: config.recurringDay,
			expiringDay: config.expiringDay,
			frequency: config.frequency,
			startDate: config.startDate,
			endDate: config.endDate,
			active: true
		}).then(res => {
			if (res.status !== 200) return;

			setLoaderDisplay(false);
			toast.success("Event updated successfully", {
				position: toast.POSITION.TOP_RIGHT,
			});
			onUpdate();
			handleClose();
		}).catch(err => {
			setLoaderDisplay(false);
		});
	};

	const handleClose = () => {
		setShow(false);
		onCloseCallback();
	};

	const resetEvent = () => {
		setConfig({
			recurringDay: null,
			expiringDay: null,
			frequency: '',
			enabled: false,
			type: '',
			startDate: '',
			endDate: ''
		});
		setShow(false);
		onCloseCallback();
	}

	return (
		<>
			<Modal
				show={show}
				onHide={handleClose}
				size="lg"
				aria-labelledby="contained-modal-title-vcenter"
				centered>
				<Modal.Header className="bg-white" closeButton>
					< Modal.Title as="h5">
						{t("EventDetails.label")}
					</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					{
						<>
							<div className="px-3 py-2">
								<div className="row m-0">
									<div className="col-lg-12 p-2">
										<label className="mb-1">{t("Frequency.label")}</label>
										<div>
											{
												config.frequency === FormFrequency.ANNUALLY.text &&
												<Form.Check
													type="radio"
													inline
													label={t("ANNUAL.label")}
													className="DataTypeCheck"
													name="typeck"
													id={`pageCheckbox-1`}
													checked={config.frequency === FormFrequency.ANNUALLY.text}
													onChange={() => setConfig({ ...config, frequency: FormFrequency.ANNUALLY.text })}
													disabled
												/>
											}

											{
												config.frequency === FormFrequency.HALF_YEARLY.text &&
												<Form.Check
													type="radio"
													inline
													name="typeck"
													id={`pageCheckbox-2`}
													label={t("HALF_YEARLY.label")}
													className="DataTypeCheck"
													checked={config.frequency === FormFrequency.HALF_YEARLY.text}
													onChange={() => setConfig({ ...config, frequency: FormFrequency.HALF_YEARLY.text })}
													disabled
												/>
											}

											{
												config.frequency === FormFrequency.QUARTERLY.text &&
												<Form.Check
													type="radio"
													inline
													name="typeck"
													id={`pageCheckbox-3`}
													label={t("QUARTERLY.label")}
													className="DataTypeCheck"
													checked={config.frequency === FormFrequency.QUARTERLY.text}
													onChange={() => setConfig({ ...config, frequency: FormFrequency.QUARTERLY.text })}
													disabled
												/>
											}

											{
												config.frequency === FormFrequency.MONTHLY.text &&
												<Form.Check
													type="radio"
													inline
													name="typeck"
													id={`pageCheckbox-4`}
													label={t("MONTHLY.label")}
													className="DataTypeCheck"
													checked={config.frequency === FormFrequency.MONTHLY.text}
													onChange={() => setConfig({ ...config, frequency: FormFrequency.MONTHLY.text })}
													disabled
												/>
											}

										</div>
									</div>
								</div>

								<div className="row m-0">
									<div className="col-lg-6 p-2">
										<label className='mb-1'> {t('Period.label')} {t('From.label')}</label>
										<MonthlyPeriodSelector defaultPeriod={config.startDate}
										type='ORG_CALENDAR'
											valueChangeCallback={(period) => {
												setConfig({ ...config, startDate: `${period.year}-${period.month}-01` });
											}} />
									</div>

									<div className="col-lg-6 p-2">
										<label className='mb-1'>{t('Period.label')} {t('To.label')}</label>
										<MonthlyPeriodSelector defaultPeriod={config.endDate}
											type='ORG_CALENDAR'
											minPeriod={config.startDate}
											valueChangeCallback={(period) => {
												const d = moment(`${period.year}-${period.month}-01`).endOf('month').format('YYYY-MM-DD');
												setConfig({ ...config, endDate: d });
											}} />
									</div>
								</div>

								<div className="row m-0">
									<div className="col-lg-12 px-2">
										<strong>{Utils.getRecurrenceText(config.frequency)}</strong>
									</div>
									<div className="col-lg-3 p-2">
										<div className='d-flex align-items-center'>
											<label className='mb-0 white-space'>Starts on day </label>
											<select className="form-select ms-2"
												value={config.recurringDay}
												disabled
												onChange={(e) => setConfig({ ...config, recurringDay: +e.target.value })}>
												{[...Array(31)].map((_, index) => {
													const day = index + 1;
													return (
														<option value={day} key={day}>
															{day}
														</option>
													);
												})}
											</select>
										</div>

									</div>
									<div className="col-lg-3 p-2">
										<div className='d-flex align-items-center'>
											<label className='mb-0 white-space'>Valid for </label>
											<input
												type="number"
												className="form-control ms-2 me-2"
												value={config.expiringDay}
												min="1" max="31"
												onChange={(e) => setConfig({ ...config, expiringDay: +e.target.value })}
												onBlur={(e) => {
													if (!e.target.value || +e.target.value < 1 || +e.target.value > 60) {
														setConfig({ ...config, expiringDay: 1 })
														toast.warning("Validity must be between 1 and 60 days", {
															position: toast.POSITION.TOP_RIGHT,
														});
													}
												}}
												disabled
											/>
											<label> day(s)</label>
										</div>
									</div>
								</div>
							</div>
						</>
					}
				</Modal.Body>
				{/* <Modal.Footer>
					<Button variant="secondary" onClick={() => resetEvent()}>
						{t("Cancel.label")}
					</Button>
					<Button variant="primary" onClick={() => updateEvent()}>
						{t("Update.label")}
					</Button>
				</Modal.Footer> */}
			</Modal>
		</>
	);
}

export default withTranslation()(OrganizationCalendarModal);